<template>
  <div>
    <ul
      v-if="parsedItems.length > 0"
      class="item-list"
    >
      <template
        v-for="item in parsedItems"
        :key="item.lineItemId"
      >
        <li
          v-if="!orderRoom.orderIsSplit || orderRoom.splitType !== 'itemized' || disableItemSelection"
          class="item-list__item"
        >
          <div class="item-list__content-wrapper">
            <div class="item-list__header">
              <h3 class="item-list__title">
                {{ item.itemDescription }}
              </h3>
              <p class="item-list__price">
                {{ $dollars(item.priceTotal) }}
              </p>
            </div>

            <div class="item-list__description">
              <order-item-description-text :item="item" />
            </div>
          </div>
        </li>



        <li
          v-else-if="item.isSelectedByOtherUser"
          class="item-list__item item-list__item--selected-by-other"
        >
          <div class="item-list__content-wrapper">
            <div class="item-list__header">
              <h3 class="item-list__title">
                {{ item.itemDescription }}
              </h3>
              <p
                v-if="item.user.paid"
                class="item-list__status"
              >
                Paid
              </p>
              <p
                v-else
                class="item-list__price"
              >
                {{ $dollars(item.priceTotal) }}
              </p>
            </div>

            <div class="item-list__description">
              <order-item-description-text :item="item" />
            </div>
          </div>
        </li>



        <li
          v-else
          class="item-list__item item-list__item--selectable"
        >
          <label
            class="item-list__content-wrapper"
            :for="item.lineItemId"
          >
            <div class="item-list__header">
              <h3 class="item-list__title">
                {{ item.itemDescription }}
              </h3>
              <p class="item-list__price">
                {{ $dollars(item.priceTotal) }}
              </p>
            </div>

            <div class="item-list__description">
              <order-item-description-text :item="item" />
            </div>

            <div class="checkbox-input checkbox-input--default checkbox-input--no-label-text">
              <div class="checkbox-input__option">
                <input
                  :id="item.lineItemId"
                  v-model="item.isSelectedByYou"
                  class="checkbox-input__input"
                  type="checkbox"
                  :name="item.lineItemId"
                  @change="selectItems"
                >

                <div class="checkbox-input__label" />
              </div>
            </div>
          </label>
        </li>
      </template>
    </ul>



    <template v-if="order.subtotal > 0 || order.orderItems.length > 0">
      <slot />

      <template v-if="orderRoom.orderIsSplit">
        <div
          class="key-value"
          @click="toggleGroupTotalDetails"
        >
          <template v-if="groupTotalDetailsAreVisible">

            <!-- Group Subtotal -->
            <dl class="key-value__row">
              <dt class="key-value__key">
                {{ computedSubstrings.groupSubtotal }}
              </dt>
              <dd class="key-value__value">
                {{ computedTotals.groupSubtotal }}
              </dd>
            </dl>

            <!-- Group Payment Applied -->
            <dl
              v-if="computedTotals.externalPaymentAmount"
              class="key-value__row"
            >
              <dt class="key-value__key">
                {{ computedSubstrings.groupPaymentApplied }}
              </dt>
              <dd class="key-value__value primary">
                -{{ computedTotals.externalPaymentAmount }}
              </dd>
            </dl>

            <!-- Group Discount -->
            <dl
              v-if="computedTotals.discount "
              class="key-value__row"
            >
              <dt class="key-value__key">
                {{ computedSubstrings.groupDiscount }}
              </dt>
              <dd class="key-value__value primary">
                -{{ computedTotals.discount }}
              </dd>
            </dl>

            <!-- Offers -->
            <div
              v-if="order.discount && order.offers.length > 0"
              class="key-value__nested-section"
            >
              <div
                v-for="offer in order.offers"
                :key="offer.id"
                class="key-value__row key-value__row--justify-start"
              >
                <p class="key-value__key">
                  {{ offer.name || offer.promotionCode }}
                </p>
                <div
                  v-if="order.orderAttributes && order.orderAttributes.canRemoveOffers"
                  class="key-value__value"
                  @click="removeOffer(offer)"
                >
                  <button
                    type="button"
                    title="Remove this discount"
                    tabindex="-1"
                    class="link danger"
                    :class="{
                      'link--loading': order.removingOfferIds.includes(offer.id)
                    }"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>

            <!-- Group Tax -->
            <dl class="key-value__row">
              <dt class="key-value__key">
                {{ computedSubstrings.groupTax }}
              </dt>
              <dd class="key-value__value">
                {{ computedTotals.groupTax }}
              </dd>
            </dl>

            <!-- Group Service Charges -->
            <dl
              v-if="computedTotals.groupServiceCharges "
              class="key-value__row"
            >
              <dt class="key-value__key">
                {{ computedSubstrings.groupServiceCharges }}
              </dt>
              <dd class="key-value__value">
                {{ computedTotals.groupServiceCharges }}
              </dd>
            </dl>

            <!-- Group Order Fees -->
            <template v-if="computedTotals.groupOrderFees">
              <dl
                v-for="{ name, amount, description } in computedTotals.groupOrderFees"
                :key="name"
                class="key-value__row"
              >
                <dt class="key-value__key">
                  {{ name }}
                  <button
                    v-if="description"
                    type="button"
                    class="cursor-pointer"
                    @click="$openModal('notice', { title: name, description, useSecondaryStyle: true, excludeButton: true })"
                  >
                    <span class="icon fal fa-info-circle gray" />
                  </button>
                </dt>
                <dd class="key-value__value">
                  {{ amount }}
                </dd>
              </dl>
            </template>
          </template>

          <!-- Group Total -->
          <dl class="key-value__row key-value__row--large">
            <dt class="key-value__key">
              {{ computedSubstrings.groupTotal }}
            </dt>
            <dd class="key-value__value">
              {{ computedTotals.groupTotal }}

              <span
                class="icon icon--inline-end far dark-gray"
                :class="{
                  'fa-angle-down': !groupTotalDetailsAreVisible,
                  'fa-angle-up': groupTotalDetailsAreVisible
                }"
              />
            </dd>
          </dl>
        </div>

        <hr>
      </template>

      <div class="key-value">
        <!-- Subtotal -->
        <dl class="key-value__row">
          <dt class="key-value__key">
            {{ computedSubstrings.subtotal }}
          </dt>
          <dd class="key-value__value">
            {{ computedTotals.yourSubtotal }}
          </dd>
        </dl>

        <!-- Discount -->
        <dl
          v-if="!orderRoom.orderIsSplit && computedTotals.discount"
          class="key-value__row"
        >
          <dt class="key-value__key">
            {{ computedSubstrings.discount }}
          </dt>
          <dd class="key-value__value primary">
            -{{ computedTotals.discount }}
          </dd>
        </dl>

        <!-- Offers -->
        <div
          v-if="!orderRoom.orderIsSplit && order.discount && order.offers.length > 0"
          class="key-value__nested-section"
        >
          <div
            v-for="offer in order.offers"
            :key="offer.id"
            class="key-value__row key-value__row--justify-start"
          >
            <p class="key-value__key">
              {{ offer.name || offer.promotionCode }}
            </p>
            <div
              v-if="order.orderAttributes && order.orderAttributes.canRemoveOffers"
              class="key-value__value"
              @click="removeOffer(offer)"
            >
              <button
                type="button"
                title="Remove this discount"
                tabindex="-1"
                class="link danger"
                :class="{
                  'link--loading': order.removingOfferIds.includes(offer.id)
                }"
              >
                Remove
              </button>
            </div>
          </div>
        </div>

        <!-- External Payment -->
        <dl
          v-if="!orderRoom.orderIsSplit && computedTotals.externalPaymentAmount"
          class="key-value__row"
        >
          <dt class="key-value__key">
            {{ computedSubstrings.externalPaymentAmount }}
          </dt>
          <dd class="key-value__value primary">
            -{{ computedTotals.externalPaymentAmount }}
          </dd>
        </dl>

        <!-- Tax -->
        <dl
          v-if="computedTotals.yourTax"
          class="key-value__row"
        >
          <dt class="key-value__key">
            {{ computedSubstrings.tax }}
          </dt>
          <dd class="key-value__value">
            {{ computedTotals.yourTax }}
          </dd>
        </dl>

        <!-- Service Charges -->
        <dl
          v-if="computedTotals.yourServiceCharges"
          class="key-value__row"
        >
          <dt class="key-value__key">
            {{ computedSubstrings.serviceCharges }}
          </dt>
          <dd class="key-value__value">
            {{ computedTotals.yourServiceCharges }}
          </dd>
        </dl>

        <!-- Order Fees -->
        <template v-if="computedTotals.yourOrderFees">
          <dl
            v-for="{ name, amount, description } in computedTotals.yourOrderFees"
            :key="name"
            class="key-value__row"
          >
            <dt class="key-value__key">
              {{ name }}
              <button
                v-if="description"
                type="button"
                class="cursor-pointer"
                @click="$openModal('notice', { title: name, description, useSecondaryStyle: true, excludeButton: true })"
              >
                <span class="icon fal fa-info-circle gray" />
              </button>
            </dt>
            <dd class="key-value__value">
              {{ amount }}
            </dd>
          </dl>
        </template>

        <!-- Tip -->
        <dl
          v-if="computedTotals.yourTip"
          class="key-value__row"
        >
          <dt class="key-value__key">
            {{ computedSubstrings.tip }}
          </dt>
          <dd class="key-value__value">
            {{ computedTotals.yourTip }}
          </dd>
        </dl>

        <!-- Credit Card Surcharge -->
        <dl
          v-if="computedTotals.yourSurcharge"
          class="key-value__row"
        >
          <dt class="key-value__key">
            {{ computedSubstrings.creditCardSurcharge }}
          </dt>
          <dd class="key-value__value">
            {{ computedTotals.yourSurcharge }}
          </dd>
        </dl>

        <!-- ExistingPayment -->
        <dl
          v-if="computedTotals.existingPaymentAmount"
          class="key-value__row"
        >
          <dt class="key-value__key primary">
            {{ computedSubstrings.payment }}
          </dt>
          <dd class="key-value__value primary">
            -{{ computedTotals.existingPaymentAmount }}
          </dd>
        </dl>

        <!-- Total -->
        <dl class="key-value__row key-value__row--large">
          <dt class="key-value__key">
            {{ computedSubstrings.total }}
          </dt>
          <dd class="key-value__value">
            {{ computedTotals.yourTotal }}
          </dd>
        </dl>
      </div>
    </template>
  </div>
</template>


<script>
  import events from '@/services/events';
  import { mapGetters } from 'vuex';
  import dollars from '@/filters/dollars';

  export default {
    name: 'OrderItemsRoom',

    props: {
      disableItemSelection: {
        type: Boolean,
        default: false
      },

      existingPaymentAmount: {
        type: Number,
        default: 0
      },

      yourTotals: {
        type: Object,
        default() {
          return {
            payments: [],
            serviceCharges: 0,
            orderFeeTotal: 0,
            orderFees: [],
            tax: 0,
            tip: 0,
            total: 0
          };
        }
      }
    },

    data() {
      return {
        groupTotalDetailsAreVisible: false,
        parsedItems: []
      };
    },

    computed: {
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('socket', ['orderRoom']),

      computedSubstrings() {
        const orderSplitSubString = this.orderRoom.orderIsSplit ? 'Your' : '';

        return {
          groupSubtotal: 'Group Subtotal',
          groupPaymentApplied: 'Group Payment Applied',
          groupDiscount: 'Group Discount',
          groupTax: 'Group Tax',
          groupServiceCharges: 'Group Service Charges',
          groupTotal: 'Group Total',
          discount: 'Discount',
          creditCardSurcharge: 'Credit Card Surcharge',
          payment: 'Payment',
          externalPaymentAmount: 'Payment Applied',
          subtotal: `${orderSplitSubString} Subtotal`,
          tax: `${orderSplitSubString} Tax`,
          serviceCharges: `${orderSplitSubString} Service Charges`,
          tip: `${orderSplitSubString} Tip`,
          total: `${orderSplitSubString} Total`
        };
      },

      computedTotals() {
        const {
          payments: yourPayments = [],
          serviceCharges: yourServiceCharges,
          orderFeeTotal: yourOrderFeeTotal,
          orderFees: yourOrderFees,
          tip: yourTip,
          tax: yourTax,
          total: yourTotal
        } = this.yourTotals;

        const {
          externalPaymentAmount,
          discount,
          serviceCharges,
          orderFees,
          subtotal,
          tax,
          total
        } = this.order;

        const yourSubTotal = yourTotal - yourTax - yourServiceCharges - yourOrderFeeTotal;

        const yourSubtotalWithDiscounts = yourSubTotal + discount + externalPaymentAmount;

        let yourCompleteSubtotal;

        if (this.orderRoom.orderIsSplit) {
          yourCompleteSubtotal = yourSubTotal >= 0 ? dollars(yourSubTotal) : dollars(0);
        }
        else {
          yourCompleteSubtotal = yourSubtotalWithDiscounts >= 0 ? dollars(yourSubtotalWithDiscounts) : dollars(0);
        }

        const groupOrderFees = orderFees.length > 0 && orderFees.map(orderFee => ({ ...orderFee, amount: dollars(orderFee.amount) }));

        const yourComputedOrderFees = yourOrderFees.length > 0 && yourOrderFees.map(fee => ({ ...fee, amount: dollars(fee.amount) }));

        const creditCardPayment = yourPayments.find(payment => payment.paymentType === 'CreditCard');

        const yourSurcharge = creditCardPayment?.surcharge
          ? creditCardPayment.surcharge
          : null;

        const computedTotal = yourSurcharge
          ? yourTotal + yourSurcharge + yourTip - this.existingPaymentAmount
          : yourTotal + yourTip - this.existingPaymentAmount;

        return {
          discount: discount && dollars(discount),
          existingPaymentAmount: this.existingPaymentAmount && dollars(this.existingPaymentAmount),
          externalPaymentAmount: externalPaymentAmount && dollars(externalPaymentAmount),
          /** Group totals */
          groupSubtotal: dollars(subtotal + discount),
          groupServiceCharges: serviceCharges && dollars(serviceCharges),
          groupOrderFees,
          groupTax: dollars(tax),
          groupTotal: dollars(total - externalPaymentAmount),
          /** Your totals */
          yourServiceCharges: yourServiceCharges && dollars(yourServiceCharges),
          /** This mostly applies to the receipt page. If an order is split, then get the users portion of their payment,
           * else get the orderFees for the order as a single payer will be responsible for the full bill.
           */
          yourSurcharge: yourSurcharge && dollars(yourSurcharge),
          yourOrderFees: this.orderRoom.orderIsSplit ? yourComputedOrderFees : groupOrderFees,
          yourSubtotal: yourCompleteSubtotal,
          yourTip: yourTip && dollars(yourTip),
          yourTax: yourTax && dollars(yourTax),
          yourTotal: dollars(computedTotal)
        };
      }
    },

    watch: {
      'order.discount': {
        handler: 'discountChangeHandler',
        immediate: true
      },
      order: 'syncSelectedItems',
      'orderRoom.orderIsSplit': 'syncSelectedItems',
      'orderRoom.otherUsers': 'syncSelectedItems',
      'orderRoom.splitType': 'syncSelectedItems',
      'orderRoom.you': 'syncSelectedItems'
    },

    created() {
      this.syncSelectedItems();
    },

    methods: {
      discountChangeHandler(newValue) {
        if (newValue) {
          this.groupTotalDetailsAreVisible = true;
        }
      },

      findUserWhoSelectedItem(itemToFind) {
        const allUsers = this.orderRoom.otherUsers.concat([this.orderRoom.you]);
        let result = null;

        for (let i = 0; i < allUsers.length; i++) {
          const user = allUsers[i];

          for (let i2 = 0; i2 < user.items.length; i2++) {
            const item = user.items[i2];

            if (item.lineItemId === itemToFind.lineItemId) {
              result = user;

              break;
            }
          }

          if (result) {
            break;
          }
        }

        return result;
      },

      removeOffer(offer) {
        this.$store.dispatch('orders/removeOffer', {
          id: offer.id
        });
      },

      selectItems() {
        const selectedItems = [];

        this.parsedItems.forEach((item) => {
          if (item.isSelectedByYou) {
            selectedItems.push({
              lineItemId: item.lineItemId,
              priceTotal: item.priceTotal
            });
          }
        });

        this.$store.dispatch('socket/selectItems', selectedItems);
      },

      syncSelectedItems() {
        let allItemsAreSelected = true;

        this.parsedItems = this.order.orderItems.slice(0);

        if (!this.orderRoom.orderIsSplit || this.orderRoom.splitType !== 'itemized' || this.disableItemSelection) {
          allItemsAreSelected = true;
        }

        else {
          this.parsedItems.forEach((item) => {
            const user = this.findUserWhoSelectedItem(item);

            if (user) {
              item.user = user;

              if (user.id === this.orderRoom.you.id) {
                item.isSelectedByOtherUser = false;
                item.isSelectedByYou = true;
              }

              else {
                item.isSelectedByOtherUser = true;
                item.isSelectedByYou = false;
              }
            }

            else {
              item.isSelectedByOtherUser = false;
              item.isSelectedByYou = false;
              item.user = null;

              allItemsAreSelected = false;
            }
          });
        }

        events.emit('order-items:all-items-selected', allItemsAreSelected);
      },

      toggleGroupTotalDetails() {
        this.groupTotalDetailsAreVisible = !this.groupTotalDetailsAreVisible;
      }
    }
  };
</script>
